import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import Layout from '../../components/layouts/Main';
import config from '../../config';
import { toast } from 'react-toastify';
import BuyerTable from '../../components/buyers/BuyerTable';
import BuyerModal from '../../components/buyers/BuyerModal';
import { PulseLoader } from "react-spinners";
import { useHasPermission, PERMISSIONS } from '../../utils/permissions';

function BuyerPage() {
  const [buyers, setBuyers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Permission checks
  const canViewBuyers = useHasPermission(PERMISSIONS.VIEW_BUYERS);
  const canAddBuyers = useHasPermission(PERMISSIONS.ADD_BUYERS);
  const canEditBuyers = useHasPermission(PERMISSIONS.EDIT_BUYERS);
  const canDeleteBuyers = useHasPermission(PERMISSIONS.DELETE_BUYERS);

  const fetchBuyers = useCallback(async () => {
    if (!canViewBuyers) {
      toast.error("You don't have permission to view buyers");
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/buyers`);
      setBuyers(res.data);
    } catch (err) {
      console.error("Error fetching buyers", err);
      toast.error("Failed to load buyers");
    } finally {
      setIsLoading(false);
    }
  }, [canViewBuyers]);

  useEffect(() => {
    fetchBuyers();
    if (sessionStorage.getItem('buyerAdded')) {
      toast.success('Buyer added successfully!');
      sessionStorage.removeItem('buyerAdded');
    }
  }, [canViewBuyers, fetchBuyers]);

  const handleBuyerAdded = () => {
    if (!canAddBuyers) {
      toast.error("You don't have permission to add buyers");
      return;
    }
    window.location.reload();
  };

  // If user can't view buyers, show permission denied
  if (!canViewBuyers) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
            <li className="breadcrumb-item active">Buyers</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to view buyers. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item active">Buyers</li>
        </ol>
      </nav>

      <Row className="mb-4">
        <Col>
          <h2>Buyers</h2>
          <small className="text-muted">
            {canEditBuyers ? "View and manage" : "View"} buyers
            {!canAddBuyers && !canEditBuyers && !canDeleteBuyers && " (read-only)"}
          </small>
        </Col>
        <Col className="text-end">
          {/* <PermissionGate
            require={PERMISSIONS.ADD_BUYERS}
            fallback={
              <Button variant="outline-primary" disabled title="You don't have permission to add buyers">
                <i className="fas fa-plus me-2" /> Add Buyer
              </Button>
            }
          > */}
          {canAddBuyers && (
            <Button onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2" /> Add Buyer
            </Button>
          )}
          {/* </PermissionGate> */}
        </Col>
      </Row>

      {isLoading ? (
        <PulseLoader
          color="#014b7cbb"
          size={9}
        />
      ) : (
        <BuyerTable 
          buyers={buyers} 
          canEdit={canEditBuyers}
          canDelete={canDeleteBuyers}
          canView={canViewBuyers}
        />
      )}

      {canAddBuyers && (
        <BuyerModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onBuyerAdded={handleBuyerAdded}
        />
      )}
    </Layout>
  );
}

export default BuyerPage;
